import type { FetchResult } from '@apollo/client';

import { useHealthChart } from '@hooks/useHealthChart';
import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import {
  useListAlertsQuery,
  GetAlertStatsDocument,
  useUpdateAlertsMutation,
  type UpdateAlertsMutation,
  type UpdateAlertsMutationVariables,
} from 'generated/alert.generated';
import { AlertStatus } from 'generated/types';

export const useListAlerts = () => {
  const { handleError } = useResponseHandler();
  const {
    state: {
      active_user_id,
      activeProfile,
      isClient,
      isPatient,
      isPractitioner,
      isVitall,
      logged_in_user_id,
      loggedInProfile,
    },
  } = useSession();

  const vitall_patient_id = activeProfile?.vitall_patient_id ?? '';
  const isViewingActivePatient = active_user_id !== logged_in_user_id;

  const response = useListAlertsQuery({
    variables: {
      input: {
        status: [
          AlertStatus.open,
          AlertStatus.cleared,
          ...(isVitall && !isViewingActivePatient
            ? [AlertStatus.archived]
            : []),
        ],
        /**
         * vp_id is undefined when:
         * a) Curator is viewing all alerts in the system
         * b) Practitioner is viewing alerts within org
         *
         * vp_id is from active profile when:
         * a) User with access (practitioner/curator/non-patient user) is viewing alerts for active profile
         * b) isPatient: Patient is viewing their own alerts
         */
        vitall_patient_id:
          isViewingActivePatient || isPatient ? vitall_patient_id : undefined,
        account_id: isClient
          ? activeProfile?.account_id
          : isPractitioner
            ? loggedInProfile?.account_id
            : undefined,
      },
      is_vitall: isVitall,
    },
    skip: !activeProfile,
  });
  if (response.error) {
    handleError(response.error);
  }

  return response;
};

export function useUpdateAlertStatuses(): [
  (
    params: UpdateAlertsMutationVariables['input']
  ) => Promise<FetchResult<UpdateAlertsMutation>>,
  boolean,
] {
  const {
    state: { activeProfile },
  } = useSession();
  const { category, code } = useHealthChart();
  const [mutate, { loading: baseLoading }] = useUpdateAlertsMutation();
  const loading = baseLoading;
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updateAlerts',
  });

  async function updateAlerts(params: UpdateAlertsMutationVariables['input']) {
    const { status } = params;

    const response = await mutate({
      variables: { input: { ...params } },
      update: (cache, { data: response }) => {
        if (!response?.updateAlerts) {
          return;
        }

        for (const { alert_id, observation_id } of response.updateAlerts) {
          // Update alert status in alerts table
          cache.modify({
            id: cache.identify({
              alert_id,
              __typename: 'AlertModel',
            }),
            fields: {
              status() {
                return status as string;
              },
            },
          });

          if (observation_id) {
            // Update alert status in health tracker drilldown
            cache.modify({
              id: cache.identify({
                __typename: 'ObservationAlertModel',
                alert_id,
                observation_id,
              }),
              fields: {
                status() {
                  return status as string;
                },
              },
            });
          }
        }
      },
      // Fetch alert stats in health tracker drilldown
      refetchQueries: [
        ...(code !== 'test'
          ? [
              {
                query: GetAlertStatsDocument,
                variables: {
                  input: {
                    account_id: activeProfile?.account_id,
                    category,
                    code,
                    statuses: [AlertStatus.open],
                    vitall_patient_id: activeProfile?.vitall_patient_id,
                  },
                },
              },
            ]
          : []),
      ],
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.updateAlerts && !errors) {
      handleSuccess();
    }
    return data ?? {};
  }
  return [updateAlerts, loading];
}
